// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/EmailDesk.jsx
//  FORMATO 06 — EL CORREO QUE DECIDE
//
//  Un correo real que llegó, un encargo con restricción de longitud, y la
//  obligación de escribir algo que resuelva sin romper la relación.
//
//  ── POR QUÉ EL LÍMITE DE PALABRAS ES EL EJERCICIO ───────────────────────────
//  Escribir educado y largo es fácil: se rellena con disculpas. Escribir
//  educado, firme y en sesenta palabras obliga a decidir qué frase se queda. Eso
//  es lo que separa un correo que se lee de uno que se archiva.
//
//  ── LA NOTA ES MIXTA, Y SE DICE CUÁL ES CUÁL ────────────────────────────────
//  La longitud la cuenta el servidor con código: es un hecho, sale exacta y no
//  cuesta un token. El registro lo juzga la IA: es una opinión informada. La
//  pantalla las muestra separadas porque un alumno tiene derecho a saber qué
//  parte de su calificación es medición y qué parte es criterio.
//
//  Es el primer sitio de toda la plataforma donde se entrena escritura.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';

import { evaluateEmail } from './advancedApi';
import { textosSala } from './salaTextos';

// `idioma` lo trae la sala, que ya lo preguntó una vez. Ver ChoiceDrill.
export default function EmailDesk({ brief, onExit, onRestart, idioma = 'es' }) {
  const S = textosSala(idioma);
  const T = S.email;
  const [draft, setDraft] = useState('');
  const [phase, setPhase] = useState('writing');   // writing|grading|verdict|error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const words = draft.trim() ? draft.trim().split(/\s+/).length : 0;
  const inRange = words >= brief.min_words && words <= brief.max_words;
  const ready = words >= 15;

  const send = async () => {
    setPhase('grading');
    setError(null);
    try {
      setResult(await evaluateEmail({ emailId: brief.id, draft: draft.trim() }));
      setPhase('verdict');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  };

  if (phase === 'error') {
    return (
      <div className="ar-screen">
        <div className="ar-fail">{error}</div>
        <div className="ar-actions">
          <button type="button" className="ar-btn" onClick={() => setPhase('writing')}>Volver a mi borrador</button>
          <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>Salir</button>
        </div>
      </div>
    );
  }

  const score = result?.score;
  const tone = score === null || score === undefined ? 'ar-v-mid'
    : score >= 70 ? 'ar-v-ok' : score >= 45 ? 'ar-v-mid' : 'ar-v-bad';

  return (
    <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-email)' }}>
      <div className="ar-hud">
        <button type="button" className="ar-quit" onClick={onExit}>{S.drill.salir}</button>
        <span className="ar-badge">{S.formatos.email.name}</span>
        <span className="ar-spacer" />
        <span className="ar-badge">{brief.level}</span>
      </div>

      <div className="ar-card-q">
        <span className="ar-lbl">{T.bandeja}</span>
        <div className="ar-inbox">{brief.inbox}</div>
        <p className="ar-task">{brief.task}</p>
        <ul className="ar-must">
          {brief.must.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </div>

      {phase === 'writing' && (
        <>
          <textarea
            className="ar-area"
            placeholder={T.escribeAqui}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2500}
          />
          <div className={`ar-wc ${inRange ? 'ar-in' : words > 0 ? 'ar-out' : ''}`}>
            <span><b>{words}</b> {T.palabras}</span>
            <span>{T.objetivoPalabras(brief.min_words, brief.max_words)}</span>
          </div>
          <p className="ar-note">{T.limite}</p>
          <div className="ar-actions">
            <button type="button" className="ar-btn" onClick={send} disabled={!ready}>
              {ready ? T.enviar : T.masLineas}
            </button>
            <button type="button" className="ar-btn ar-btn-ghost" onClick={onRestart}>{T.otroEncargo}</button>
          </div>
        </>
      )}

      {phase === 'grading' && (
        <>
          <div className="ar-spin" />
          <p className="ar-lbl" style={{ textAlign: 'center' }}>{T.revisando}</p>
        </>
      )}

      {phase === 'verdict' && result && (
        <>
          <div className={`ar-verdict ${tone}`} style={{ marginTop: '.9rem' }}>
            {score !== null && score !== undefined
              ? <p className="ar-score ar-num">{Math.round(score)}<small>/100</small></p>
              : <p className="ar-lbl">{T.sinCalificar}</p>}

            <p className="ar-said">{result.verdict_es}</p>

            {result.strengths?.length > 0 && (
              <ul className="ar-list">
                {result.strengths.map((s) => <li key={s} className="ar-plus">{s}</li>)}
              </ul>
            )}
            {result.improve?.length > 0 && (
              <ul className="ar-list">
                {result.improve.map((s) => <li key={s} className="ar-minus">{s}</li>)}
              </ul>
            )}

            {/* Las dos mitades de la nota, separadas a propósito. */}
            <div className="ar-stats">
              <div>
                <b className="ar-num" style={{ color: result.length_ok ? 'var(--ar-go)' : 'var(--ar-hot)' }}>
                  {result.words}
                </b>
                <span>{T.palabrasMedido}</span>
              </div>
              {result.register_score !== null && result.register_score !== undefined && (
                <div>
                  <b className="ar-num">{Math.round(result.register_score)}</b>
                  <span>{T.registroJuzgado}</span>
                </div>
              )}
              <div>
                <b className="ar-num">{result.min_words}–{result.max_words}</b>
                <span>{T.objetivo}</span>
              </div>
            </div>

            <p className="ar-note">{result.length_note}</p>

            {result.ai_evaluated === false && (
              <p className="ar-note">{T.sinIA}</p>
            )}
          </div>

          <div className="ar-actions">
            <button type="button" className="ar-btn" onClick={() => { setResult(null); setPhase('writing'); }}>
              {T.reescribir}
            </button>
            <button type="button" className="ar-btn ar-btn-ghost" onClick={onRestart}>{T.otroEncargo}</button>
          </div>
        </>
      )}
    </div>
  );
}
